import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';

import { Betaaltransactie, BetaaltransactieStatus, Betaalverzoek, BetaalverzoekResource } from '@ap/interface-apic-betaalverzoek';
import { SessiebeheerResource, SessionStatus } from '@ap/interface-apic-sessiebeheer';
import { Geld } from '@ap/interface-bspc-core';

import { BetaalverzoekErrorType, ErrorData } from './betaalverzoek.model';
import { BetaalverzoekNavigation } from './betaalverzoek.navigation';

/**
 * @ngdoc service
 * @name BetaalverzoekService
 * @module BetaalverzoekModule
 *
 * @description
 * Service to store and retrieve the status for the result page
 **/
@Injectable({
    providedIn: 'root'
})
export class BetaalverzoekService {
    private readonly showHeaderIcon: BehaviorSubject<boolean>;

    private transactieId;
    private toegangscode;

    public constructor(
        private readonly betaalverzoekNavigation: BetaalverzoekNavigation,
        private readonly betaalverzoekResource: BetaalverzoekResource,
        private readonly sessiebeheerResource: SessiebeheerResource
    ) {
        this.showHeaderIcon = new BehaviorSubject<boolean>(true);
    }

    public static generateErrorData(errorType: BetaalverzoekErrorType, showReloadOption: boolean): ErrorData {
        return {
            errorType,
            showReloadOption
        };
    }

    public getBetaalverzoek(betaalverzoekId: string, autorisatieCode: string): Observable<Betaalverzoek> {
        return this.terminateSession().pipe(concatMap(() => this.betaalverzoekResource.getBetaalverzoek(betaalverzoekId, autorisatieCode)));
    }

    public updateAndGetBetaaltransactie(transactieId: string, toegangscode: string): Observable<Betaaltransactie> {
        return this.terminateSession().pipe(
            concatMap(() => this.betaalverzoekResource.updateBetaaltransactie(transactieId, toegangscode)),
            concatMap(() => this.getBetaaltransactie(transactieId, toegangscode)),
            catchError((error) => throwError(() => error))
        );
    }

    public getBetaaltransactie(transactionId: string, toegangscode: string): Observable<Betaaltransactie> {
        return this.betaalverzoekResource.getBetaaltransactie(transactionId, toegangscode);
    }

    public createBetaaltransactie(
        betaalverzoekId: string,
        autorisatieCode: string,
        selectedBic: string,
        amount: number
    ): Observable<string> {
        const betaaltransactie: Betaaltransactie = {
            autorisatie_code: autorisatieCode,
            betaalverzoek_id: betaalverzoekId,
            ...(amount && {
                bedrag: {
                    bedrag: amount
                } as Geld
            })
        };
        if (selectedBic) {
            betaaltransactie.debiteur = { bic: selectedBic };
        }
        return this.terminateSession().pipe(
            concatMap(() => this.betaalverzoekResource.createBetaaltransactie(betaaltransactie)),
            map((response: HttpResponse<void>) => response.headers.get('location'))
        );
    }

    public navigateToErrorPageOnInvalidStatus(betaaltransactieStatus: BetaaltransactieStatus): void {
        switch (betaaltransactieStatus) {
            case BetaaltransactieStatus.SUCCESS: {
                break;
            }
            case BetaaltransactieStatus.OPEN: {
                break;
            }
            // known transaction failures
            case BetaaltransactieStatus.FAILED: {
                this.betaalverzoekNavigation.toErrorPage(
                    BetaalverzoekService.generateErrorData(BetaalverzoekErrorType.TRANSACTION_STATUS_IS_FAILED, false)
                );
                break;
            }
            case BetaaltransactieStatus.EXPIRED: {
                this.betaalverzoekNavigation.toErrorPage(
                    BetaalverzoekService.generateErrorData(BetaalverzoekErrorType.TRANSACTION_STATUS_IS_EXPIRED, false)
                );
                break;
            }
            case BetaaltransactieStatus.CANCELLED: {
                this.betaalverzoekNavigation.toErrorPage(
                    BetaalverzoekService.generateErrorData(BetaalverzoekErrorType.TRANSACTION_STATUS_IS_CANCELLED, false)
                );
                break;
            }
            default: {
                this.betaalverzoekNavigation.toErrorPage(
                    BetaalverzoekService.generateErrorData(BetaalverzoekErrorType.APPLICATION_GENERIC_ERROR, false)
                );
            }
        }
    }

    public getShowHeaderIcon(): Observable<boolean> {
        return this.showHeaderIcon.asObservable();
    }

    public setShowHeaderIcon(value: boolean) {
        this.showHeaderIcon.next(value);
    }

    public setTransactieId(transactieId: string): void {
        this.transactieId = transactieId;
    }

    public getTransactieId(): string {
        return this.transactieId;
    }

    public setToegangscode(toegangscode: string): void {
        this.toegangscode = toegangscode;
    }

    public getToegangscode(): string {
        return this.toegangscode;
    }

    public terminateSession(): Observable<void> {
        return this.sessiebeheerResource.patchSession('0', { status: SessionStatus.TERMINATED });
    }
}
